import {clsx} from 'clsx'
import {useId} from 'react'

import type {Universe26Artwork} from '../../marketing-global-banner.types'
import monaHead from './assets/universe_26-visual-on-d-mona-head.webp'
import styles from './Universe26BannerVisual.module.css'

type ShapeGroup = {
  fill: string
  paths: string[]
  delay: number
  /** Horizontal start offset (px) for the slide-in animation. */
  slideFrom?: number
  /** Raster layer painted over the group's paths so both move as a unit. */
  image?: {href: string; x: number; width: number}
}

type ArtworkVariant = {
  width: number
  groups: ShapeGroup[]
  className: string | undefined
  /** Left edge of the viewBox, for variants cropped from wider artwork. */
  minX?: number
  glow?: boolean
}

type ArtworkDefinition = {
  animationClassName: string | undefined
  variants: ArtworkVariant[]
  /** Keeps artwork without built-in clear space from running under the close button. */
  clearOfClose?: boolean
}

const single = ({d, fill, delay}: {d: string; fill: string; delay: number}): ShapeGroup => ({fill, paths: [d], delay})

// Keep the supplied artwork's paint order; delays follow its left-to-right wave instead.
const virtDesktop = [
  {d: 'M208.23 21H247.407V42H208.23V21Z', fill: '#583AF7', delay: 650},
  {d: 'M78.047 28H104.165V42H78.047V28Z', fill: '#583AF7', delay: 200},
  {d: 'M156.098 14H182.216V28H156.098V14Z', fill: '#180E27', delay: 550},
  {d: 'M247.406 0H286.583V21H247.406V0Z', fill: '#0FBF3E', delay: 700},
  {d: 'M104.063 14H130.181V28H104.063V14Z', fill: '#0FBF3E', delay: 350},
  {d: 'M156.098 28H182.216V42H156.098V28Z', fill: '#08872B', delay: 500},
  {d: 'M0.000144958 28H26.1182V42H0.000144958V28Z', fill: '#08872B', delay: 0},
  {d: 'M182.113 0H208.231V14H182.113V0Z', fill: '#AE85FF', delay: 600},
  {d: 'M130.082 28H156.2V42H130.082V28Z', fill: '#AE85FF', delay: 400},
  {d: 'M78.047 0H104.165V14H78.047V0Z', fill: '#AE85FF', delay: 250},
  {d: 'M26.0158 28H52.1338V42H26.0158V28Z', fill: '#AE85FF', delay: 50},
  {d: 'M130.082 0H156.2V14H130.082V0Z', fill: '#C05200', delay: 450},
  {d: 'M104.063 28H130.181V42H104.063V28Z', fill: '#BBA00A', delay: 300},
  {d: 'M52.0314 28H78.1494V42H52.0314V28Z', fill: '#BBA00A', delay: 100},
  {d: 'M52.0314 14H78.1494V28H52.0314V14Z', fill: '#F73678', delay: 150},
].map(single)

const virtMobile = [
  {d: 'M78.049 28H104.167V42H78.049V28Z', fill: '#583AF7', delay: 200},
  {d: 'M104.065 14H130.183V28H104.065V14Z', fill: '#0FBF3E', delay: 300},
  {d: 'M0.00209808 28H26.1201V42H0.00209808V28Z', fill: '#08872B', delay: 0},
  {d: 'M78.049 0H104.167V14H78.049V0Z', fill: '#AE85FF', delay: 250},
  {d: 'M26.0177 28H52.1357V42H26.0177V28Z', fill: '#AE85FF', delay: 50},
  {d: 'M52.0335 28H78.1515V42H52.0335V28Z', fill: '#BBA00A', delay: 100},
  {d: 'M52.0335 14H78.1515V28H52.0335V14Z', fill: '#F73678', delay: 150},
].map(single)

// Supplied 42x42 squares are written as equivalent paths. Groups are in paint order (right to left);
// each slides in from just past the artwork's right edge, so later pieces land on top of earlier ones.
const TOM_STAGGER = 150

const tomDesktop: ShapeGroup[] = [
  {
    fill: '#BBA00A',
    slideFrom: 137,
    paths: [
      'M243 0H285V42H243V0Z',
      'M285 42H321.011C320.923 40.1961 320.601 38.787 319.385 37.4199C318.014 35.8764 316.252 34.5702 315.437 32.6109C312.924 26.5587 319.558 22.6737 323.382 19.4859C325.515 17.7072 324.959 15.1116 324.959 15.1116C324.959 15.1116 327.414 12.6063 326.939 10.8675C326.464 9.1287 324.883 10.0779 324.883 10.0779C324.883 10.0779 320.219 14.0301 316.464 14.0301C316.464 14.0301 310.893 0 299.192 0H285V42Z',
    ],
  },
  {
    fill: '#5FED83',
    slideFrom: 168.5,
    paths: [
      'M211.5 0H253.5V42H211.5V0Z',
      'M295.5 22.3671C295.5 19.4418 294.15 17.6484 292.466 16.3716C292.797 16.1133 292.993 15.6891 292.928 15.2229C292.852 14.6895 292.415 14.259 291.884 14.1897C291.161 14.0973 290.544 14.6412 290.508 15.3405C289.893 15.12 289.355 15.0129 289.003 14.9772C288.851 14.9625 288.723 14.8659 288.665 14.7231C287.955 12.999 283.291 2.5368 274.559 2.5368C270.636 2.5368 268.933 2.8644 266.22 3.78C266.113 3.8157 265.991 3.8514 265.892 3.7989L265.481 3.2907C263.899 1.3419 261.98 0 257.505 0H253.5V42C253.5 42 270.569 42 275.708 42C293.574 36.4644 295.5 26.1618 295.5 22.3671Z',
    ],
  },
  {
    fill: '#583AF7',
    slideFrom: 200,
    paths: [
      'M180 0H222V42H180V0Z',
      'M222 42H258.011C257.923 40.1961 257.601 38.787 256.385 37.4199C255.014 35.8764 253.252 34.5702 252.437 32.6109C249.924 26.5587 256.558 22.6737 260.382 19.4859C262.515 17.7072 261.959 15.1116 261.959 15.1116C261.959 15.1116 264.414 12.6063 263.939 10.8675C263.464 9.1287 261.883 10.0779 261.883 10.0779C261.883 10.0779 257.219 14.0301 253.464 14.0301C253.464 14.0301 247.893 0 236.192 0H222V42Z',
    ],
  },
  {
    fill: '#C05200',
    slideFrom: 231.5,
    paths: [
      'M148.5 0H190.5V42H148.5V0Z',
      'M232.5 22.3671C232.5 19.4418 231.15 17.6484 229.466 16.3716C229.797 16.1133 229.993 15.6891 229.928 15.2229C229.852 14.6895 229.415 14.259 228.884 14.1897C228.161 14.0973 227.544 14.6412 227.508 15.3405C226.893 15.12 226.355 15.0129 226.003 14.9772C225.851 14.9625 225.723 14.8659 225.665 14.7231C224.955 12.999 220.291 2.5368 211.559 2.5368C207.636 2.5368 205.933 2.8644 203.22 3.78C203.113 3.8157 202.991 3.8514 202.892 3.7989L202.481 3.2907C200.899 1.3419 198.98 0 194.505 0H190.5V42C190.5 42 207.569 42 212.708 42C230.574 36.4644 232.5 26.1618 232.5 22.3671Z',
    ],
  },
  {
    fill: '#F73678',
    slideFrom: 263,
    paths: [
      'M159 0H117L144 9L140.5 42H159V0Z',
      'M159 42H195.011C194.923 40.1961 194.601 38.787 193.385 37.4199C192.014 35.8764 190.252 34.5702 189.437 32.6109C186.924 26.5587 193.558 22.6737 197.382 19.4859C199.515 17.7072 198.959 15.1116 198.959 15.1116C198.959 15.1116 201.414 12.6063 200.939 10.8675C200.464 9.1287 198.883 10.0779 198.883 10.0779C198.883 10.0779 194.219 14.0301 190.464 14.0301C190.464 14.0301 184.893 0 173.192 0H159V42Z',
    ],
  },
  {
    fill: '#AE85FF',
    slideFrom: 270,
    paths: [
      'M131.501 0C135.976 3.60014e-05 137.895 1.34224 139.477 3.29102L139.889 3.79883C139.987 3.85105 140.109 3.81593 140.216 3.78027C142.929 2.86469 144.632 2.53712 148.555 2.53711C157.286 2.53711 161.95 12.9969 162.66 14.7227C162.719 14.8655 162.848 14.9628 162.999 14.9775C163.352 15.0133 163.889 15.1205 164.504 15.3408C164.54 14.6415 165.158 14.0971 165.88 14.1895C166.411 14.2587 166.848 14.6894 166.924 15.2227C166.989 15.6889 166.794 16.1138 166.462 16.3721C168.146 17.6488 169.496 19.4421 169.496 22.3672C169.496 26.162 167.57 36.4644 149.703 42H132.007C131.919 40.1961 131.597 38.787 130.381 37.4199C129.01 35.8766 127.248 34.5703 126.434 32.6113C123.92 26.5594 130.553 22.674 134.377 19.4863C136.511 17.7076 135.954 15.1113 135.954 15.1113C135.954 15.1113 138.409 12.606 137.935 10.8672C137.46 9.12888 135.879 10.0781 135.879 10.0781C135.851 10.102 131.203 14.0303 127.46 14.0303C127.46 14.0303 121.889 0 110.188 0H131.501Z',
    ],
  },
].map((group, index) => ({...group, delay: index * TOM_STAGGER}))

const tomMobile: ShapeGroup[] = [
  {
    fill: '#BBA00A',
    slideFrom: 124,
    paths: [
      'M73 0H115V42H73V0Z',
      'M115 42H151.011C150.923 40.1961 150.601 38.787 149.385 37.4199C148.014 35.8764 146.252 34.5702 145.437 32.6109C142.924 26.5587 149.558 22.6737 153.382 19.4859C155.515 17.7072 154.959 15.1116 154.959 15.1116C154.959 15.1116 157.414 12.6063 156.939 10.8675C156.464 9.1287 154.883 10.0779 154.883 10.0779C154.883 10.0779 150.219 14.0301 146.464 14.0301C146.464 14.0301 140.893 0 129.192 0H115V42Z',
    ],
  },
  {
    fill: '#5FED83',
    slideFrom: 155.5,
    paths: [
      'M41.5 0H83.5V42H41.5V0Z',
      'M125.5 22.3671C125.5 19.4418 124.15 17.6484 122.466 16.3716C122.797 16.1133 122.993 15.6891 122.928 15.2229C122.852 14.6895 122.415 14.259 121.884 14.1897C121.161 14.0973 120.544 14.6412 120.508 15.3405C119.893 15.12 119.355 15.0129 119.003 14.9772C118.851 14.9625 118.723 14.8659 118.665 14.7231C117.955 12.999 113.291 2.5368 104.559 2.5368C100.636 2.5368 98.9329 2.8644 96.2197 3.78C96.1126 3.8157 95.9908 3.8514 95.8921 3.7989L95.4805 3.2907C93.8992 1.3419 91.9798 0 87.5047 0H83.5V42C83.5 42 100.569 42 105.708 42C123.574 36.4644 125.5 26.1618 125.5 22.3671Z',
    ],
  },
  {
    fill: '#583AF7',
    slideFrom: 187,
    paths: [
      'M52 0H10L36.5 9L31.5 42H52V0Z',
      'M52 42H88.0108C87.9226 40.1961 87.6013 38.787 86.3854 37.4199C85.0141 35.8764 83.2522 34.5702 82.4374 32.6109C79.9237 26.5587 86.5576 22.6737 90.3817 19.4859C92.5153 17.7072 91.9588 15.1116 91.9588 15.1116C91.9588 15.1116 94.4137 12.6063 93.9391 10.8675C93.4645 9.1287 91.8832 10.0779 91.8832 10.0779C91.8832 10.0779 87.2191 14.0301 83.4643 14.0301C83.4643 14.0301 77.893 0 66.1918 0H52V42Z',
    ],
  },
  {
    fill: '#C05200',
    slideFrom: 194,
    paths: [
      'M24.502 0C28.9766 0.000133631 30.8963 1.34231 32.4775 3.29102L32.8896 3.79883C32.9882 3.85076 33.11 3.81588 33.2168 3.78027C35.9297 2.8648 37.6332 2.53712 41.5557 2.53711C50.2862 2.5375 54.9506 12.997 55.6611 14.7227C55.72 14.8654 55.8489 14.9628 56 14.9775C56.3528 15.0133 56.8902 15.1206 57.5049 15.3408C57.5406 14.6415 58.1585 14.0971 58.8809 14.1895C59.4119 14.2589 59.8491 14.6895 59.9248 15.2227C59.9899 15.6887 59.7945 16.1138 59.4629 16.3721C61.1467 17.6488 62.4971 19.4423 62.4971 22.3672C62.497 26.1619 60.5704 36.4644 42.7041 42H25.0068C24.9186 40.1962 24.5976 38.787 23.3818 37.4199C22.0106 35.8765 20.2485 34.5704 19.4336 32.6113C16.9199 26.5592 23.5538 22.6741 27.3779 19.4863C29.5115 17.7076 28.9551 15.1113 28.9551 15.1113C28.9822 15.0836 31.4075 12.5963 30.9355 10.8672C30.4609 9.12866 28.8789 10.0781 28.8789 10.0781C28.8337 10.1164 24.1964 14.0303 20.46 14.0303C20.46 14.0303 14.8887 0 3.1875 0H24.502Z',
    ],
  },
].map((group, index) => ({...group, delay: index * TOM_STAGGER}))

// Delays follow each piece's horizontal position (rightmost first) without changing paint order.
function rightToLeft(shapes: Array<{d: string; fill: string}>, stagger: number, slideFrom: number): ShapeGroup[] {
  const left = (d: string) => Number(/^M(-?[\d.]+)/.exec(d)?.[1] ?? 0)
  const order = [...shapes].sort((a, b) => left(b.d) - left(a.d))
  return shapes.map(({d, fill}) => ({fill, paths: [d], slideFrom, delay: order.findIndex(s => s.d === d) * stagger}))
}

const KEYN_STAGGER = 40
const KEYN_SLIDE_FROM = 48

const keynDesktop = rightToLeft(
  [
    {d: 'M364.935 0H443.289V42H364.935V0Z', fill: '#D9D9D9'},
    {d: 'M156.093 14H182.211V28H156.093V14Z', fill: '#5FED83'},
    {d: 'M52.0304 28H78.1484V42H52.0304V28Z', fill: '#5FED83'},
    {d: 'M325.753 0H364.93V21H325.753V0Z', fill: '#D9D9D9'},
    {d: 'M104.062 28H130.18V42H104.062V28Z', fill: '#180E27'},
    {d: 'M325.753 21H364.93V42H325.753V21Z', fill: '#08872B'},
    {d: 'M286.581 21H325.758V42H286.581V21Z', fill: '#583AF7'},
    {d: 'M52.0304 0H78.1484V14H52.0304V0Z', fill: '#583AF7'},
    {d: 'M247.401 0H286.578V21H247.401V0Z', fill: '#C05200'},
    {d: 'M78.046 14H104.164V28H78.046V14Z', fill: '#C05200'},
    {d: 'M25.8117 14H51.9297V28H25.8117V14Z', fill: '#D9D9D9'},
    {d: 'M-0.000831604 28H26.1172V42H-0.000831604V28Z', fill: '#C05200'},
    {d: 'M182.109 0H208.227V14H182.109V0Z', fill: '#BBA00A'},
    {d: 'M182.109 27.8906H208.227V41.8906H182.109V27.8906Z', fill: '#08872B'},
    {d: 'M130.077 28H156.195V42H130.077V28Z', fill: '#AE85FF'},
    {d: 'M78.046 0H104.164V14H78.046V0Z', fill: '#AE85FF'},
    {d: 'M52.0304 14H78.1484V28H52.0304V14Z', fill: '#BBA00A'},
    {d: 'M443.287 0H521.641V42H443.287V0Z', fill: '#583AF7'},
    {d: 'M521.646 0H600V42H521.646V0Z', fill: '#180E27'},
  ],
  KEYN_STAGGER,
  KEYN_SLIDE_FROM,
)

const keynTablet = rightToLeft(
  [
    {d: 'M234.943 0H313.297V42H234.943V0Z', fill: '#583AF7'},
    {d: 'M26.1007 14H52.2188V28H26.1007V14Z', fill: '#5FED83'},
    {d: 'M195.76 0H234.938V21H195.76V0Z', fill: '#D9D9D9'},
    {d: 'M195.76 21H234.938V42H195.76V21Z', fill: '#08872B'},
    {d: 'M156.589 21H195.766V42H156.589V21Z', fill: '#583AF7'},
    {d: 'M117.409 0H156.586V21H117.409V0Z', fill: '#C05200'},
    {d: 'M52.1164 0H78.2344V14H52.1164V0Z', fill: '#BBA00A'},
    {d: 'M52.1164 27.8906H78.2344V41.8906H52.1164V27.8906Z', fill: '#08872B'},
    {d: 'M0.0851059 28H26.2031V42H0.0851059V28Z', fill: '#AE85FF'},
    {d: 'M313.294 0H391.648V42H313.294V0Z', fill: '#D9D9D9'},
  ],
  KEYN_STAGGER,
  KEYN_SLIDE_FROM,
)

const keynMobile = rightToLeft(
  [
    {d: 'M130.28 14H156.398V28H130.28V14Z', fill: '#5FED83'},
    {d: 'M26.2179 28H52.3359V42H26.2179V28Z', fill: '#5FED83'},
    {d: 'M26.2179 0H52.3359V14H26.2179V0Z', fill: '#583AF7'},
    {d: 'M52.2335 14H78.3516V28H52.2335V14Z', fill: '#C05200'},
    {d: 'M-0.000831604 14H26.1172V28H-0.000831604V14Z', fill: '#D9D9D9'},
    {d: 'M156.296 0H182.414V14H156.296V0Z', fill: '#BBA00A'},
    {d: 'M156.296 27.8906H182.414V41.8906H156.296V27.8906Z', fill: '#08872B'},
    {d: 'M104.265 28H130.383V42H104.265V28Z', fill: '#AE85FF'},
    {d: 'M52.2335 0H78.3516V14H52.2335V0Z', fill: '#AE85FF'},
    {d: 'M26.2179 14H52.3359V28H26.2179V14Z', fill: '#BBA00A'},
  ],
  KEYN_STAGGER,
  KEYN_SLIDE_FROM,
)

// Listed right to left, in the supplied artwork's paint order.
const ON_D_STAGGER = 150

const onDShapes = [
  {
    fill: '#BBA00A',
    left: 354,
    d: 'M425.531 -24.561C422.631 -25.1571 418.899 -19.6009 417.594 -17.4739C415.892 -14.6991 414.556 -11.3726 411.557 -9.74449C408.487 -8.07829 404.665 -8.49003 401.306 -8.44922C392.286 -8.33918 379.855 -4.91921 374.65 3.05507C373.979 4.08349 373.447 5.19948 372.756 6.21461C372.206 7.02111 371.561 7.75655 370.963 8.52821C366.352 14.4819 365.937 21.9559 364.009 28.9765C363.57 30.5776 362.659 35.5455 361.535 36.1292C361.094 34.9439 358.974 34.6945 358.419 35.9458C357.974 36.9499 358.932 37.9173 358.694 38.33C357.392 38.9288 354.354 43.39 356.312 45.2992C355.573 46.6908 354.824 48.1593 354.387 49.8842C352.901 55.7517 356.176 61.3541 358.465 64.6022C365.22 74.1876 375.606 80.3274 389.027 83.2174L388.981 83.3091C387.602 83.9954 385.668 87.088 384.811 88.4901C382.26 92.6643 380.634 97.6794 379.267 103.071C378.916 104.859 378.565 106.647 378.213 108.435H436.862V108.389C434.068 100.756 431.819 94.5648 427.24 88.7652C425.98 87.1701 424.277 85.5929 422.612 84.4095C421.65 83.7983 420.688 83.1866 419.726 82.5755V82.5296L422.429 81.6126C426.19 80.1371 429.354 78.4824 432.372 76.3398C444.829 67.4962 451.058 52.3597 450.636 37.275C450.433 29.9939 448.625 22.7679 445.418 16.2311C442.201 9.67493 437.547 4.00187 435.136 -2.97883C432.852 -9.58952 431.19 -16.4987 427.701 -22.6211C427.014 -23.8279 426.278 -24.4079 425.532 -24.5615L425.531 -24.561Z',
  },
  {
    fill: '#AE85FF',
    left: 324,
    d: 'M395.297 -24.561C392.396 -25.1571 388.665 -19.6009 387.36 -17.4739C385.658 -14.6991 384.322 -11.3726 381.322 -9.74449C378.253 -8.07829 374.431 -8.49003 371.072 -8.44922C362.051 -8.33918 349.621 -4.91921 344.415 3.05507C343.744 4.08349 343.213 5.19948 342.521 6.21461C341.972 7.02111 341.326 7.75655 340.728 8.52821C336.117 14.4819 335.703 21.9559 333.775 28.9765C333.336 30.5776 332.425 35.5455 331.301 36.1292C330.86 34.9439 328.739 34.6945 328.185 35.9458C327.74 36.9499 328.698 37.9173 328.46 38.33C327.157 38.9288 324.12 43.39 326.077 45.2992C325.338 46.6908 324.59 48.1593 324.153 49.8842C322.667 55.7517 325.942 61.3541 328.231 64.6022C334.986 74.1876 345.371 80.3274 358.792 83.2174L358.746 83.3091C357.368 83.9954 355.434 87.088 354.577 88.4901C352.026 92.6643 350.4 97.6794 349.033 103.071C348.681 104.859 348.33 106.647 347.979 108.435H406.628V108.389C403.834 100.756 401.585 94.5648 397.006 88.7652C395.746 87.1701 394.043 85.5929 392.378 84.4095C391.416 83.7983 390.454 83.1866 389.491 82.5755V82.5296L392.195 81.6126C395.956 80.1371 399.119 78.4824 402.137 76.3398C414.595 67.4962 420.824 52.3597 420.402 37.275C420.198 29.9939 418.39 22.7679 415.183 16.2311C411.966 9.67493 407.313 4.00187 404.901 -2.97883C402.618 -9.58952 400.956 -16.4987 397.467 -22.6211C396.779 -23.8279 396.043 -24.4079 395.298 -24.5615L395.297 -24.561Z',
  },
  {
    fill: '#F73678',
    left: 294,
    d: 'M365.055 -24.561C362.154 -25.1571 358.423 -19.6009 357.118 -17.4739C355.415 -14.6991 354.08 -11.3726 351.08 -9.74449C348.011 -8.07829 344.188 -8.49003 340.83 -8.44922C331.809 -8.33918 319.378 -4.91921 314.173 3.05507C313.502 4.08349 312.971 5.19948 312.279 6.21461C311.73 7.02111 311.084 7.75655 310.486 8.52821C305.875 14.4819 305.46 21.9559 303.533 28.9765C303.093 30.5776 302.182 35.5455 301.059 36.1292C300.618 34.9439 298.497 34.6945 297.943 35.9458C297.497 36.9499 298.456 37.9173 298.218 38.33C296.915 38.9288 293.878 43.39 295.835 45.2992C295.096 46.6908 294.348 48.1593 293.911 49.8842C292.424 55.7517 295.7 61.3541 297.989 64.6022C304.744 74.1876 315.129 80.3274 328.55 83.2174L328.504 83.3091C327.126 83.9954 325.191 87.088 324.335 88.4901C321.784 92.6643 320.158 97.6794 318.79 103.071C318.439 104.859 318.088 106.647 317.737 108.435H376.385V108.389C373.592 100.756 371.343 94.5648 366.763 88.7652C365.504 87.1701 363.8 85.5929 362.136 84.4095C361.174 83.7983 360.211 83.1866 359.249 82.5755V82.5296L361.952 81.6126C365.714 80.1371 368.877 78.4824 371.895 76.3398C384.352 67.4962 390.582 52.3597 390.16 37.275C389.956 29.9939 388.148 22.7679 384.941 16.2311C381.724 9.67493 377.07 4.00187 374.659 -2.97883C372.375 -9.58952 370.714 -16.4987 367.225 -22.6211C366.537 -23.8279 365.801 -24.4079 365.055 -24.5615L365.055 -24.561Z',
  },
  {
    fill: '#C05200',
    left: 264,
    d: 'M334.821 -24.561C331.92 -25.1571 328.188 -19.6009 326.883 -17.4739C325.181 -14.6991 323.845 -11.3726 320.846 -9.74449C317.776 -8.07829 313.954 -8.49003 310.596 -8.44922C301.575 -8.33918 289.144 -4.91921 283.939 3.05507C283.268 4.08349 282.736 5.19948 282.045 6.21461C281.495 7.02111 280.85 7.75655 280.252 8.52821C275.641 14.4819 275.226 21.9559 273.298 28.9765C272.859 30.5776 271.948 35.5455 270.824 36.1292C270.383 34.9439 268.263 34.6945 267.708 35.9458C267.263 36.9499 268.221 37.9173 267.983 38.33C266.681 38.9288 263.643 43.39 265.601 45.2992C264.862 46.6908 264.113 48.1593 263.676 49.8842C262.19 55.7517 265.465 61.3541 267.754 64.6022C274.509 74.1876 284.895 80.3274 298.316 83.2174L298.27 83.3091C296.892 83.9954 294.957 87.088 294.1 88.4901C291.549 92.6643 289.923 97.6794 288.556 103.071C288.205 104.859 287.854 106.647 287.502 108.435H346.151V108.389C343.358 100.756 341.108 94.5648 336.529 88.7652C335.27 87.1701 333.566 85.5929 331.901 84.4095C330.939 83.7983 329.977 83.1866 329.015 82.5755V82.5296L331.718 81.6126C335.479 80.1371 338.643 78.4824 341.661 76.3398C354.118 67.4962 360.347 52.3597 359.925 37.275C359.722 29.9939 357.914 22.7679 354.707 16.2311C351.49 9.67493 346.836 4.00187 344.425 -2.97883C342.141 -9.58952 340.479 -16.4987 336.991 -22.6211C336.303 -23.8279 335.567 -24.4079 334.821 -24.5615L334.821 -24.561Z',
  },
  {
    fill: '#583AF7',
    left: 233,
    d: 'M304.586 -24.561C301.685 -25.1571 297.954 -19.6009 296.649 -17.4739C294.947 -14.6991 293.611 -11.3726 290.611 -9.74449C287.542 -8.07829 283.72 -8.49003 280.361 -8.44922C271.34 -8.33918 258.91 -4.91921 253.705 3.05507C253.033 4.08349 252.502 5.19948 251.81 6.21461C251.261 7.02111 250.615 7.75655 250.018 8.52821C245.406 14.4819 244.992 21.9559 243.064 28.9765C242.625 30.5776 241.714 35.5455 240.59 36.1292C240.149 34.9439 238.028 34.6945 237.474 35.9458C237.029 36.9499 237.987 37.9173 237.749 38.33C236.446 38.9288 233.409 43.39 235.366 45.2992C234.627 46.6908 233.879 48.1593 233.442 49.8842C231.956 55.7517 235.231 61.3541 237.52 64.6022C244.275 74.1876 254.66 80.3274 268.081 83.2174L268.035 83.3091C266.657 83.9954 264.723 87.088 263.866 88.4901C261.315 92.6643 259.689 97.6794 258.322 103.071C257.97 104.859 257.619 106.647 257.268 108.435H315.917V108.389C313.123 100.756 310.874 94.5648 306.295 88.7652C305.035 87.1701 303.332 85.5929 301.667 84.4095C300.705 83.7983 299.743 83.1866 298.78 82.5755V82.5296L301.484 81.6126C305.245 80.1371 308.408 78.4824 311.426 76.3398C323.884 67.4962 330.113 52.3597 329.691 37.275C329.487 29.9939 327.679 22.7679 324.473 16.2311C321.256 9.67493 316.602 4.00187 314.19 -2.97883C311.907 -9.58952 310.245 -16.4987 306.756 -22.6211C306.068 -23.8279 305.333 -24.4079 304.587 -24.5615L304.586 -24.561Z',
  },
  {
    fill: '#5FED83',
    left: 203,
    d: 'M274.352 -24.561C271.451 -25.1571 267.719 -19.6009 266.415 -17.4739C264.712 -14.6991 263.377 -11.3726 260.377 -9.74449C257.308 -8.07829 253.485 -8.49003 250.127 -8.44922C241.106 -8.33918 228.675 -4.91921 223.47 3.05507C222.799 4.08349 222.267 5.19948 221.576 6.21461C221.027 7.02111 220.381 7.75655 219.783 8.52821C215.172 14.4819 214.757 21.9559 212.83 28.9765C212.39 30.5776 211.479 35.5455 210.355 36.1292C209.915 34.9439 207.794 34.6945 207.24 35.9458C206.794 36.9499 207.752 37.9173 207.515 38.33C206.212 38.9288 203.175 43.39 205.132 45.2992C204.393 46.6908 203.645 48.1593 203.208 49.8842C201.721 55.7517 204.996 61.3541 207.286 64.6022C214.041 74.1876 224.426 80.3274 237.847 83.2174L237.801 83.3091C236.423 83.9954 234.488 87.088 233.631 88.4901C231.081 92.6643 229.455 97.6794 228.087 103.071C227.736 104.859 227.385 106.647 227.034 108.435H285.682V108.389C282.889 100.756 280.64 94.5648 276.06 88.7652C274.801 87.1701 273.097 85.5929 271.433 84.4095C270.47 83.7983 269.508 83.1866 268.546 82.5755V82.5296L271.249 81.6126C275.011 80.1371 278.174 78.4824 281.192 76.3398C293.649 67.4962 299.878 52.3597 299.456 37.275C299.253 29.9939 297.445 22.7679 294.238 16.2311C291.021 9.67493 286.367 4.00187 283.956 -2.97883C281.672 -9.58952 280.01 -16.4987 276.522 -22.6211C275.834 -23.8279 275.098 -24.4079 274.352 -24.5615L274.352 -24.561Z',
  },
  {
    fill: '#180E27',
    left: 176,
    d: 'M247.383 -24.561C244.482 -25.1571 240.751 -19.6009 239.446 -17.4739C237.744 -14.6991 236.408 -11.3726 233.408 -9.74449C230.339 -8.07829 226.517 -8.49003 223.158 -8.44922C214.137 -8.33918 201.706 -4.91921 196.501 3.05507C195.83 4.08349 195.299 5.19948 194.607 6.21461C194.058 7.02111 193.412 7.75655 192.814 8.52821C188.203 14.4819 187.788 21.9559 185.861 28.9765C185.421 30.5776 184.511 35.5455 183.387 36.1292C182.946 34.9439 180.825 34.6945 180.271 35.9458C179.826 36.9499 180.784 37.9173 180.546 38.33C179.243 38.9288 176.206 43.39 178.163 45.2992C177.424 46.6908 176.676 48.1593 176.239 49.8842C174.753 55.7517 178.028 61.3541 180.317 64.6022C187.072 74.1876 197.457 80.3274 210.878 83.2174L210.832 83.3091C209.454 83.9954 207.52 87.088 206.663 88.4901C204.112 92.6643 202.486 97.6794 201.119 103.071C200.767 104.859 200.416 106.647 200.065 108.435H258.714V108.389C255.92 100.756 253.671 94.5648 249.092 88.7652C247.832 87.1701 246.128 85.5929 244.464 84.4095C243.502 83.7983 242.539 83.1866 241.577 82.5755V82.5296L244.281 81.6126C248.042 80.1371 251.205 78.4824 254.223 76.3398C266.681 67.4962 272.91 52.3597 272.488 37.275C272.284 29.9939 270.476 22.7679 267.269 16.2311C264.052 9.67493 259.399 4.00187 256.987 -2.97883C254.704 -9.58952 253.042 -16.4987 249.553 -22.6211C248.865 -23.8279 248.129 -24.4079 247.383 -24.5615L247.383 -24.561Z',
  },
]

// The head's body lines up with the black shape's right edge, so it rides in that shape's group.
const onDMonaHead = {href: monaHead, x: 142, width: 162}

const ON_D_GLOW =
  'M226.534 126.042L92.5672 51.1722L39.6338 -55.2916L64.8229 -69.6502L184.594 -96.4519L218.169 -99.1457L205.652 -71.6718L195.743 -55.8992L185.975 -31.5229L185.037 -20.7806L178.135 -7.25396L214.908 88.4582L205.904 114.09L226.534 126.042Z'

function onDGroups(fills: string[]): ShapeGroup[] {
  const shapes = onDShapes.filter(({fill}) => fills.includes(fill))
  return shapes.map(({fill, d}, index) => ({
    fill,
    paths: [d],
    // Mona (leftmost) fades in first, then each shape to its right.
    delay: (shapes.length - 1 - index) * ON_D_STAGGER,
    ...(fill === '#180E27' ? {image: onDMonaHead} : {}),
  }))
}

const ALL_ON_D_FILLS = onDShapes.map(({fill}) => fill)
// Tablet drops the three rightmost shapes; mobile keeps only the black shape and Mona.
const TABLET_ON_D_FILLS = ['#C05200', '#583AF7', '#5FED83', '#180E27']

const artworks: Record<Universe26Artwork, ArtworkDefinition> = {
  virt: {
    animationClassName: styles.waveIn,
    variants: [
      {width: 197, groups: virtMobile, className: styles.belowLarge},
      {width: 380, groups: virtDesktop, className: styles.largeUp},
    ],
  },
  tom: {
    animationClassName: styles.flipIn,
    variants: [
      {width: 197, groups: tomMobile, className: styles.belowLarge},
      {width: 380, groups: tomDesktop, className: styles.largeUp},
    ],
  },
  keyn: {
    animationClassName: styles.slideIn,
    clearOfClose: true,
    variants: [
      {width: 183, groups: keynMobile, className: styles.mobileOnly},
      {width: 392, groups: keynTablet, className: styles.tabletOnly},
      {width: 600, groups: keynDesktop, className: styles.desktopUp},
    ],
  },
  // Desktop keeps the supplied 88px right-hand clear space; tablet and mobile crops keep the same gap.
  'on-d': {
    animationClassName: styles.fadeIn,
    variants: [
      {minX: 171, width: 150, groups: onDGroups(['#180E27']), className: styles.mobileOnly},
      {width: 448, groups: onDGroups(TABLET_ON_D_FILLS), className: styles.tabletOnly, glow: true},
      {width: 539, groups: onDGroups(ALL_ON_D_FILLS), className: styles.desktopUp, glow: true},
    ],
  },
}

export function Universe26BannerVisual({artwork = 'virt'}: {artwork?: Universe26Artwork}) {
  const {animationClassName, variants, clearOfClose} = artworks[artwork]

  return (
    <div className={clsx(styles.visualContainer, clearOfClose && styles.clearOfClose)}>
      {variants.map(({width, minX = 0, groups, className, glow}) => (
        <svg
          key={width}
          className={clsx(styles.visualImage, className)}
          width={width}
          height="42"
          viewBox={`${minX} 0 ${width} 42`}
          preserveAspectRatio="xMaxYMid slice"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          {groups.map(({fill, paths, delay, slideFrom, image}) => (
            <g
              key={paths[0]}
              fill={fill}
              className={animationClassName}
              style={{
                animationDelay: `${delay}ms`,
                ...(slideFrom === undefined ? {} : {'--slide-from': `${slideFrom}px`}),
              }}
            >
              {paths.map(d => (
                <path key={d} d={d} fillRule="evenodd" clipRule="evenodd" />
              ))}
              {image ? <image href={image.href} x={image.x} y="0" width={image.width} height="42" /> : null}
            </g>
          ))}
          {glow ? <OnDGlow delay={groups.length * ON_D_STAGGER} /> : null}
        </svg>
      ))}
    </div>
  )
}

/** Soft light beam from the supplied artwork; fades in once the shapes have landed. */
function OnDGlow({delay}: {delay: number}) {
  const id = useId()
  const filterId = `${id}-blur`
  const gradientId = `${id}-gradient`

  return (
    <>
      <defs>
        <filter
          id={filterId}
          x="0.00281143"
          y="-138.776"
          width="266.158"
          height="304.448"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation="19.815" />
        </filter>
        <linearGradient
          id={gradientId}
          x1="129.245"
          y1="-13.8444"
          x2="185.33"
          y2="-91.4099"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g opacity="0.3" filter={`url(#${filterId})`} style={{mixBlendMode: 'plus-lighter'}}>
        <g className={styles.fadeIn} style={{animationDelay: `${delay}ms`}}>
          <path d={ON_D_GLOW} fill={`url(#${gradientId})`} />
        </g>
      </g>
    </>
  )
}
